import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { patientApi, vitalApi, medicationApi, labApi, aiApi, formsApi, patientTabApi, patientFieldApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  User, Calendar, MessageSquare, FileText, Activity, Pill, AlertTriangle, Clock,
  ChevronLeft, Heart, Brain, Phone, Mail, MapPin, Shield, Send, FormInput, Eye,
  Edit, Save, Folder, FolderPlus, Plus, Trash2, Loader2, Building2, Stethoscope,
  Languages, UserCheck, Home, Thermometer, Printer, Download, ChevronDown, ChevronRight,
  Image as ImageIcon, Paperclip, Camera, Settings, Mic
} from 'lucide-react';
import PatientFileUpload from './PatientFileUpload';
import VoiceNoteDialog from '../../components/voice/VoiceNoteDialog';
import PatientCustomTabContent from '../../components/PatientCustomTabContent';
import FormSectionsView from '../../components/forms/FormSectionsView';
import FormFilledView from '../../components/forms/FormFilledView';
import PrintPatientHeader from '../../components/forms/PrintPatientHeader';
import PrintPreviewDialog from '../../components/forms/PrintPreviewDialog';
import PatientFormRecords from '../../components/forms/PatientFormRecords';

// Built-in tab registry (for override-aware rendering)
const BUILTIN_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'hospital', label: 'Recent Hospitalization' },
  { key: 'concern', label: 'Chief Concern' },
  { key: 'caregiver', label: 'Caregiver' },
  { key: 'pharmacy', label: 'Pharmacy' },
  { key: 'clinical', label: 'Clinical' },
  { key: 'files', label: 'Files' },
];

// Lazy-loaded thumbnail for uploaded patient files. Fetches the base64 data_url on mount
// so that the /files list response stays compact.
function UploadThumb({ patientId, uploadId, filename }) {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await patientApi.getUpload(patientId, uploadId);
        if (!cancelled) setSrc(r.data.data_url);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, uploadId]);
  if (error) return <div className="text-[10px] text-[#991B1B]">load error</div>;
  if (!src) return <Loader2 className="w-5 h-5 text-[#0E7490] animate-spin" />;
  return (
    <button
      type="button"
      onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}
      className="w-full h-full"
      title={filename}
      data-testid={`upload-thumb-${uploadId}`}
    >
      <img src={src} alt={filename} className="w-full h-full object-cover" />
    </button>
  );
}

// Collapsible form sections view imported from shared component at top.
// (previous inline copy removed — see /components/forms/FormSectionsView.js)

export default function PatientProfile() {
  const { patientId } = useParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [careGaps, setCareGaps] = useState([]);
  const [vitals, setVitals] = useState([]);
  const [medications, setMedications] = useState([]);
  const [labs, setLabs] = useState([]);
  const [patientForms, setPatientForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiSummary, setAiSummary] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showVoiceNote, setShowVoiceNote] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState({ folders: [], files: [], uploads: [] });
  const [customTabs, setCustomTabs] = useState([]);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showAddTab, setShowAddTab] = useState(false);
  const [newTabName, setNewTabName] = useState('');
  const [newTabFormId, setNewTabFormId] = useState('');
  const [allForms, setAllForms] = useState([]);
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [showAddVital, setShowAddVital] = useState(false);
  const [vitalForm, setVitalForm] = useState({ blood_pressure_systolic: '', blood_pressure_diastolic: '', heart_rate: '', temperature: '', oxygen_saturation: '', weight: '', respiratory_rate: '', recorded_at: '' });
  const [showSubmission, setShowSubmission] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [exportForms, setExportForms] = useState([]);
  const [printMode, setPrintMode] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  // map of tabKey -> boolean. Keys: 'overview','hospital',...,'custom-<id>','form-<id>'
  const [selectedPrintTabs, setSelectedPrintTabs] = useState({});
  // 'print' (browser print), 'download' (PDF), or 'email' (PDF to provider)
  const [deliveryMode, setDeliveryMode] = useState('print');
  const [emailForm, setEmailForm] = useState({ recipient_email: '', recipient_name: '', message: '' });
  const [sendingPdf, setSendingPdf] = useState(false);

  const isAdmin = ['super_admin', 'practice_admin'].includes(currentUser?.role);
  // All employees can edit custom-field values (with a full audit
  // trail). Patients themselves remain read-only on their own profile.
  const canEdit = currentUser?.role && currentUser.role !== 'patient';

  // Split tabs by type: custom-fields tabs, form-linked tabs, built-in overrides
  const customFieldsTabs = useMemo(
    () => customTabs.filter(t => t.type !== 'form_linked' && t.type !== 'builtin_override' && !t.hidden),
    [customTabs]
  );
  const formLinkedTabs = useMemo(
    () => customTabs.filter(t => t.type === 'form_linked' && !t.hidden),
    [customTabs]
  );
  const builtinOverrides = useMemo(() => {
    const map = {};
    for (const t of customTabs) {
      if (t.type === 'builtin_override' && t.builtin_key) map[t.builtin_key] = t;
    }
    return map;
  }, [customTabs]);
  // Built-in tabs that aren't hidden by an override
  const visibleBuiltinTabs = useMemo(
    () => BUILTIN_TABS.filter(b => !(builtinOverrides[b.key]?.hidden)),
    [builtinOverrides]
  );
  const builtinLabel = (key, fallback) => builtinOverrides[key]?.name || fallback;

  // Per-field disable check — returns true if admin toggled this built-in field off.
  const isBuiltinFieldDisabled = (tabKey, fieldKey) => {
    const o = builtinOverrides[tabKey];
    if (!o || !Array.isArray(o.disabled_fields)) return false;
    return o.disabled_fields.includes(fieldKey);
  };

  // Render the admin-added custom fields block at the bottom of a built-in tab.
  const builtinExtras = (key) => {
    const o = builtinOverrides[key];
    if (!o || !(o.fields && o.fields.length)) return null;
    return (
      <div className="mt-6 pt-4 border-t border-[#E2E8F0]" data-testid={`builtin-extras-${key}`}>
        <h4 className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-3 flex items-center gap-2">
          <Settings className="w-3 h-3" />
          Custom Fields
        </h4>
        <PatientCustomTabContent tab={o} patientId={patientId} canEdit={canEdit} editing={editing} />
      </div>
    );
  };

  useEffect(() => { loadPatientData(); }, [patientId]);

  const loadPatientData = async () => {
    setLoading(true);
    try {
      const [patientRes, timelineRes, careGapsRes, vitalsRes, medsRes, labsRes, formsRes, filesRes, tabsRes, fieldsRes, allFormsRes] = await Promise.all([
        patientApi.get(patientId),
        patientApi.getTimeline(patientId),
        patientApi.getCareGaps(patientId),
        vitalApi.list(patientId),
        medicationApi.list(patientId),
        labApi.list(patientId),
        formsApi.patientForms(patientId).catch(() => ({ data: { submissions: [] } })),
        patientApi.getFiles(patientId).catch(() => ({ data: { folders: [], files: [], uploads: [] } })),
        patientTabApi.list().catch(() => ({ data: { tabs: [] } })),
        patientFieldApi.list().catch(() => ({ data: { fields: [] } })),
        formsApi.list().catch(() => ({ data: { forms: [] } }))
      ]);
      setPatient(patientRes.data);
      setTimeline(timelineRes.data.timeline || []);
      setCareGaps(careGapsRes.data.care_gaps || []);
      setVitals(vitalsRes.data || []);
      setMedications(medsRes.data || []);
      setLabs(labsRes.data.labs || []);
      setPatientForms(formsRes.data.submissions || []);
      setFiles(filesRes.data || { folders: [], files: [], uploads: [] });
      setCustomTabs(tabsRes.data.tabs || []);
      setCustomFieldDefs(fieldsRes.data.fields || []);
      setAllForms(allFormsRes.data.forms || []);
      // Auto-generate form tabs from published forms assigned to patient's org
    } catch (error) {
      toast.error('Failed to load patient data');
    } finally { setLoading(false); }
  };

  const startEdit = () => { setEditForm({ ...patient }); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setEditForm({}); };
  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const updates = {};
      for (const [k, v] of Object.entries(editForm)) {
        if (k !== 'id' && k !== 'mrn' && k !== 'risk_score' && k !== 'organization_id' && k !== 'created_at' && v !== patient[k]) {
          updates[k] = v;
        }
      }
      if (Object.keys(updates).length === 0) { setEditing(false); setSaving(false); return; }
      await patientApi.update(patientId, updates);
      toast.success('Patient updated');
      setEditing(false);
      loadPatientData();
    } catch (err) { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    await patientApi.createFolder(patientId, newFolderName);
    setNewFolderName(''); setShowNewFolder(false);
    const res = await patientApi.getFiles(patientId);
    setFiles(res.data);
    toast.success('Folder created');
  };

  const handleMoveFile = async (fileId, folderId) => {
    await patientApi.moveFile(patientId, fileId, folderId);
    const res = await patientApi.getFiles(patientId);
    setFiles(res.data);
  };

  const handleAddTab = async () => {
    if (!newTabName.trim()) return;
    await patientTabApi.create({ name: newTabName, form_id: newTabFormId || null, type: newTabFormId ? 'form' : 'custom' });
    setNewTabName(''); setNewTabFormId(''); setShowAddTab(false);
    const res = await patientTabApi.list();
    setCustomTabs(res.data.tabs || []);
    toast.success('Tab added');
  };

  const handleDeleteTab = async (tabId) => {
    await patientTabApi.delete(tabId);
    const res = await patientTabApi.list();
    setCustomTabs(res.data.tabs || []);
    toast.success('Tab removed');
  };

  const generateAiSummary = async () => {
    setAiLoading(true);
    try {
      const content = `Patient: ${patient.first_name} ${patient.last_name}, DOB: ${patient.date_of_birth}. Vitals: ${vitals.length > 0 ? `BP: ${vitals[0].blood_pressure_systolic}/${vitals[0].blood_pressure_diastolic}` : 'N/A'}. Meds: ${medications.map(m => m.name).join(', ') || 'None'}.`;
      const response = await aiApi.generateCarePlan(content);
      setAiSummary(response.data.suggestions);
    } catch { toast.error('Failed to generate AI summary'); }
    finally { setAiLoading(false); }
  };

  const handleAddVital = async () => {
    const data = { patient_id: patientId };
    for (const [k, v] of Object.entries(vitalForm)) {
      if (k === 'recorded_at') {
        if (v) data.recorded_at = new Date(v).toISOString();
        continue;
      }
      if (v) data[k] = Number(v);
    }
    if (Object.keys(data).length <= 1) { toast.error('Enter at least one vital'); return; }
    try {
      await vitalApi.create(data);
      toast.success('Vital recorded');
      setShowAddVital(false);
      setVitalForm({ blood_pressure_systolic: '', blood_pressure_diastolic: '', heart_rate: '', temperature: '', oxygen_saturation: '', weight: '', respiratory_rate: '', recorded_at: '' });
      const res = await vitalApi.list(patientId);
      setVitals(res.data || []);
    } catch { toast.error('Failed to record vital'); }
  };

  const handleExport = () => {
    const p = patient;
    let content = `PATIENT RECORD EXPORT\n${'='.repeat(50)}\n`;
    content += `Name: ${p.first_name} ${p.last_name}\nMRN: ${p.mrn}\nDOB: ${p.date_of_birth}\nGender: ${p.gender || ''}\n`;
    content += `Phone: ${p.phone || ''}\nEmail: ${p.email || ''}\nAddress: ${p.address || ''}\n`;
    content += `Insurance: ${p.insurance_provider || ''} (${p.insurance_id || ''})\n`;
    content += `Emergency: ${p.emergency_contact_name || ''} - ${p.emergency_contact_phone || ''} (${p.emergency_contact_relationship || ''})\n`;
    content += `Practitioner: ${p.medical_practitioner || ''}\nLanguage: ${p.primary_language || ''}\n`;
    content += `\nVITALS (${vitals.length})\n${'-'.repeat(30)}\n`;
    vitals.slice(0, 10).forEach(v => {
      content += `${v.recorded_at ? new Date(v.recorded_at).toLocaleString() : ''} | BP: ${v.blood_pressure_systolic || ''}/${v.blood_pressure_diastolic || ''} HR: ${v.heart_rate || ''} Temp: ${v.temperature || ''} SpO2: ${v.oxygen_saturation || ''}%\n`;
    });
    // Include all form record data (from published forms as patient records)
    const formIds = exportForms.length > 0 ? exportForms : [...new Set(patientForms.map(pf => pf.form_id))];
    if (formIds.length > 0) {
      content += `\nPATIENT RECORDS\n${'-'.repeat(30)}\n`;
      formIds.forEach(formId => {
        const subs = patientForms.filter(s => s.form_id === formId);
        const formName = allForms.find(f => f.id === formId)?.name || subs[0]?.form_name || formId;
        content += `\n** ${formName} (${subs.length} record${subs.length !== 1 ? 's' : ''}) **\n`;
        subs.forEach(s => {
          content += `  Date: ${s.submitted_at ? new Date(s.submitted_at).toLocaleString() : ''}\n`;
          const resolved = s.resolved_responses || {};
          if (Object.keys(resolved).length > 0) {
            Object.entries(resolved).forEach(([label, val]) => {
              content += `    ${label}: ${val === null || val === undefined ? '' : String(val)}\n`;
            });
          } else {
            Object.entries(s.responses || {}).forEach(([k, v]) => {
              content += `    ${k}: ${JSON.stringify(v)}\n`;
            });
          }
        });
      });
    }
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${p.first_name}_${p.last_name}_export.txt`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExport(false);
    toast.success('Export downloaded');
  };

  // Build the list of printable tabs for the selection dialog.
  const allPrintTabs = useMemo(() => {
    const items = [];
    visibleBuiltinTabs.forEach(b => items.push({ key: b.key, label: builtinLabel(b.key, b.label) }));
    customTabs
      .filter(t => t.type !== 'builtin_override' && !t.hidden)
      .forEach(ct => items.push({ key: `custom-${ct.id}`, label: ct.name }));
    allForms
      .filter(f => f.status === 'published' && !customTabs.some(ct => ct.form_id === f.id))
      .forEach(f => items.push({ key: `form-${f.id}`, label: f.name }));
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleBuiltinTabs, customTabs, allForms, builtinOverrides]);

  // Open the dialog with every tab pre-selected.
  const openPrintDialog = () => {
    const init = {};
    allPrintTabs.forEach(t => { init[t.key] = true; });
    setSelectedPrintTabs(init);
    setDeliveryMode('print');
    setEmailForm({ recipient_email: '', recipient_name: '', message: '' });
    setShowPrintDialog(true);
  };

  const togglePrintTab = (key) => setSelectedPrintTabs(prev => ({ ...prev, [key]: !prev[key] }));
  const setAllPrintTabs = (v) => {
    const next = {};
    allPrintTabs.forEach(t => { next[t.key] = v; });
    setSelectedPrintTabs(next);
  };

  const getSelectedTabKeys = () => Object.entries(selectedPrintTabs).filter(([, v]) => v).map(([k]) => k);

  // Browser print flow — now opens an in-app preview modal instead of the
  // bare window.print() dialog so the user can see the exact printed output
  // (running header, page layout) BEFORE committing paper to it.  The modal
  // reuses the same patient-print-mode body class + collapsed-section reveal,
  // and triggers window.print() from inside the iframe on demand.
  const _runBrowserPrint = async () => {
    setShowPrintDialog(false);
    setPrintMode(true);
    document.body.classList.add('patient-print-mode');
    // Give the flex-wrap layout a beat to settle so the preview clone has
    // the fully-expanded tabs (forceMount + collapsed-section CSS).
    await new Promise(r => setTimeout(r, 400));
    setShowPrintPreview(true);
  };

  const handleClosePrintPreview = () => {
    setShowPrintPreview(false);
    // Tear down the printMode state so the on-screen view returns to normal.
    document.body.classList.remove('patient-print-mode');
    setPrintMode(false);
  };

  // Download a server-generated PDF (blob) and save via temporary link.
  const _runDownloadPdf = async (tabKeys) => {
    try {
      setSendingPdf(true);
      const res = await patientApi.recordPdf(patientId, { tabs: tabKeys, delivery: 'download' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${patient.first_name}_${patient.last_name}_summary.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
      setShowPrintDialog(false);
    } catch (err) {
      toast.error('Failed to generate PDF');
    } finally { setSendingPdf(false); }
  };

  // Email a server-generated PDF to a referring provider.
  const _runEmailPdf = async (tabKeys) => {
    const email = emailForm.recipient_email.trim();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid recipient email');
      return;
    }
    try {
      setSendingPdf(true);
      await patientApi.recordPdf(patientId, {
        tabs: tabKeys,
        delivery: 'email',
        recipient_email: email,
        recipient_name: emailForm.recipient_name.trim() || null,
        message: emailForm.message.trim() || null,
      });
      toast.success(`PDF queued for ${email}`);
      setShowPrintDialog(false);
    } catch (err) {
      toast.error('Failed to queue email');
    } finally { setSendingPdf(false); }
  };

  const handlePrintSelected = async () => {
    const tabKeys = getSelectedTabKeys();
    if (tabKeys.length === 0) { toast.error('Select at least one tab'); return; }
    if (deliveryMode === 'email') return _runEmailPdf(tabKeys);
    if (deliveryMode === 'download') return _runDownloadPdf(tabKeys);
    return _runBrowserPrint();
  };

  // Helper — returns the extra className for a TabsContent when printing with
  // a partial selection. When not in print mode the class is a no-op.
  const pclass = (key) => (printMode && selectedPrintTabs[key] === false ? 'print-exclude' : '');

  // forceMount prop for TabsContent when printing so every tab renders in DOM.
  const pmount = printMode ? { forceMount: true } : {};

  const getRiskBadge = (score) => {
    if (score >= 70) return <span className="risk-badge-high">High Risk: {score}</span>;
    if (score >= 40) return <span className="risk-badge-medium">Medium Risk: {score}</span>;
    return <span className="risk-badge-low">Low Risk: {score}</span>;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" /></div>;
  if (!patient) return <div className="text-center py-12"><Link to="/patients" className="text-[#0E7490]">Back to patients</Link></div>;

  const ef = editing ? editForm : patient;

  return (
    <div className="space-y-6" data-testid="patient-profile">
      {/* Running print header — pinned to every printed page via CSS @page.
          Repeats the FULL patient overview on every page. Hidden on screen. */}
      <PrintPatientHeader patient={patient} />

      <Link to="/patients" className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#0A2342] no-print">
        <ChevronLeft className="w-4 h-4" />Back to Patients
      </Link>

      {/* Header */}
      <Card className="careiq-card">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-[#0E7490] rounded-2xl flex items-center justify-center text-white text-xl font-semibold">
                {patient.first_name?.charAt(0)}{patient.last_name?.charAt(0)}
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {patient.first_name} {patient.last_name}
                </h1>
                <div className="text-sm text-[#64748B] mt-1">MRN: {patient.mrn} | DOB: {patient.date_of_birth} | {patient.gender}</div>
                <div className="mt-1">{getRiskBadge(patient.risk_score)}</div>
              </div>
            </div>
            <div className="flex gap-2 no-print">
              <Button variant="outline" onClick={() => setShowVoiceNote(true)} data-testid="patient-voice-note-btn"><Mic className="w-4 h-4 mr-2" />Voice Note</Button>
              <Button variant="outline" onClick={openPrintDialog} data-testid="print-patient-record-btn"><Printer className="w-4 h-4 mr-2" />Print / Share</Button>
              <Button variant="outline" onClick={() => setShowExport(true)} data-testid="export-patient-btn"><Download className="w-4 h-4 mr-2" />Export</Button>
              {!editing ? (
                <Button variant="outline" onClick={startEdit} data-testid="edit-patient-btn"><Edit className="w-4 h-4 mr-2" />Edit</Button>
              ) : (
                <>
                  <Button variant="outline" onClick={cancelEdit}>Cancel</Button>
                  <Button className="careiq-btn-primary" onClick={handleSaveEdit} disabled={saving} data-testid="save-patient-btn">
                    <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card className="careiq-card">
        <Tabs defaultValue={visibleBuiltinTabs[0]?.key || 'overview'}>
          <CardHeader className="pb-0">
            <div className="flex items-center justify-between">
              <TabsList className="flex flex-wrap h-auto gap-1">
                {visibleBuiltinTabs.map(b => (
                  <TabsTrigger key={b.key} value={b.key} data-testid={`tab-${b.key}`}>
                    {builtinLabel(b.key, b.label)}
                  </TabsTrigger>
                ))}
                {customFieldsTabs.map(ct => (
                  <TabsTrigger key={ct.id} value={`custom-${ct.id}`} data-testid={`tab-custom-${ct.id}`}>
                    {ct.name}
                  </TabsTrigger>
                ))}
                {formLinkedTabs.map(ct => (
                  <TabsTrigger key={ct.id} value={`custom-${ct.id}`} data-testid={`tab-custom-${ct.id}`}>
                    {ct.name}
                  </TabsTrigger>
                ))}
                {allForms.filter(f => f.status === 'published' && !customTabs.some(ct => ct.form_id === f.id)).map(f => (
                  <TabsTrigger key={`form-${f.id}`} value={`form-${f.id}`}>{f.name}</TabsTrigger>
                ))}
              </TabsList>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate('/admin/patient-tabs')}
                  data-testid="add-tab-btn"
                  title="Manage patient record tabs"
                  className="flex items-center gap-1 text-[#0E7490]"
                >
                  <Settings className="w-3 h-3" />
                  <Plus className="w-3 h-3" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            {/* Overview Tab */}
            <TabsContent value="overview" className={`m-0 space-y-4 ${pclass('overview')}`} {...pmount}>
              <div className="print-tab-heading">{builtinLabel('overview', 'Overview')}</div>
              <div className="grid md:grid-cols-2 gap-4">
                <FieldGroup title="Contact Information">
                  {!isBuiltinFieldDisabled('overview','email') && <EditableField label="Email" icon={Mail} value={ef.email} editing={editing} onChange={v => setEditForm({...editForm, email: v})} />}
                  {!isBuiltinFieldDisabled('overview','phone') && <EditableField label="Phone" icon={Phone} value={ef.phone} editing={editing} onChange={v => setEditForm({...editForm, phone: v})} />}
                  {!isBuiltinFieldDisabled('overview','address') && <EditableField label="Address" icon={MapPin} value={ef.address} editing={editing} onChange={v => setEditForm({...editForm, address: v})} />}
                  {!isBuiltinFieldDisabled('overview','primary_language') && <EditableField label="Primary Language" icon={Languages} value={ef.primary_language} editing={editing} onChange={v => setEditForm({...editForm, primary_language: v})} />}
                </FieldGroup>
                <FieldGroup title="Medical">
                  {!isBuiltinFieldDisabled('overview','medical_diagnosis') && <EditableField label="Medical Diagnosis" icon={Activity} value={ef.medical_diagnosis} editing={editing} onChange={v => setEditForm({...editForm, medical_diagnosis: v})} multiline />}
                  {!isBuiltinFieldDisabled('overview','medical_practitioner') && <EditableField label="Medical Practitioner" icon={Stethoscope} value={ef.medical_practitioner} editing={editing} onChange={v => setEditForm({...editForm, medical_practitioner: v})} />}
                </FieldGroup>
                <FieldGroup title="Insurance & Provider">
                  {!isBuiltinFieldDisabled('overview','insurance_provider') && <EditableField label="Insurance Provider" icon={Shield} value={ef.insurance_provider} editing={editing} onChange={v => setEditForm({...editForm, insurance_provider: v})} />}
                  {!isBuiltinFieldDisabled('overview','insurance_id') && <EditableField label="Insurance ID" value={ef.insurance_id} editing={editing} onChange={v => setEditForm({...editForm, insurance_id: v})} />}
                </FieldGroup>
                <FieldGroup title="Emergency Contact">
                  {!isBuiltinFieldDisabled('overview','emergency_contact_name') && <EditableField label="Name" icon={UserCheck} value={ef.emergency_contact_name} editing={editing} onChange={v => setEditForm({...editForm, emergency_contact_name: v})} />}
                  {!isBuiltinFieldDisabled('overview','emergency_contact_phone') && <EditableField label="Phone" icon={Phone} value={ef.emergency_contact_phone} editing={editing} onChange={v => setEditForm({...editForm, emergency_contact_phone: v})} />}
                  {!isBuiltinFieldDisabled('overview','emergency_contact_relationship') && <EditableField label="Relationship" value={ef.emergency_contact_relationship} editing={editing} onChange={v => setEditForm({...editForm, emergency_contact_relationship: v})} />}
                </FieldGroup>
                {/* Custom fields */}
                {customFieldDefs.length > 0 && (
                  <FieldGroup title="Additional Information">
                    {customFieldDefs.map(fd => (
                      <EditableField key={fd.key} label={fd.label} value={ef.custom_fields?.[fd.key] || ''} editing={editing} onChange={v => setEditForm({...editForm, custom_fields: {...(editForm.custom_fields || {}), [fd.key]: v}})} />
                    ))}
                  </FieldGroup>
                )}
              </div>
              <PatientFormRecords patientId={patientId} />
              {builtinExtras('overview')}
            </TabsContent>

            {/* Hospital Tab */}
            <TabsContent value="hospital" className={`m-0 space-y-4 ${pclass('hospital')}`} {...pmount}>
              <div className="print-tab-heading">{builtinLabel('hospital', 'Recent Hospitalization')}</div>
              <div className="grid md:grid-cols-2 gap-4">
                <FieldGroup title="Recent Hospitalization">
                  {!isBuiltinFieldDisabled('hospital','hospital_name') && <EditableField label="Hospital Name" icon={Building2} value={ef.hospital_name} editing={editing} onChange={v => setEditForm({...editForm, hospital_name: v})} />}
                  {!isBuiltinFieldDisabled('hospital','admission_date') && <EditableField label="Admission Date" value={ef.admission_date} editing={editing} onChange={v => setEditForm({...editForm, admission_date: v})} type="date" />}
                  {!isBuiltinFieldDisabled('hospital','discharge_date') && <EditableField label="Discharge Date" value={ef.discharge_date} editing={editing} onChange={v => setEditForm({...editForm, discharge_date: v})} type="date" />}
                </FieldGroup>
                <FieldGroup title="Medical History">
                  {!isBuiltinFieldDisabled('hospital','medical_history') && <EditableField label="Medical History" value={ef.medical_history} editing={editing} onChange={v => setEditForm({...editForm, medical_history: v})} multiline />}
                  {!isBuiltinFieldDisabled('hospital','allergies') && <EditableField label="Allergies" value={ef.allergies} editing={editing} onChange={v => setEditForm({...editForm, allergies: v})} multiline />}
                </FieldGroup>
              </div>
              {builtinExtras('hospital')}
            </TabsContent>

            {/* Chief Concern */}
            <TabsContent value="concern" className={`m-0 ${pclass('concern')}`} {...pmount}>
              <div className="print-tab-heading">{builtinLabel('concern', 'Chief Concern')}</div>
              <FieldGroup title="Chief Concern">
                {!isBuiltinFieldDisabled('concern','chief_concern') && <EditableField label="Chief Concern" value={ef.chief_concern} editing={editing} onChange={v => setEditForm({...editForm, chief_concern: v})} multiline />}
                {!isBuiltinFieldDisabled('concern','last_visit_date') && <EditableField label="Last Visit Date" value={ef.last_visit_date} editing={editing} onChange={v => setEditForm({...editForm, last_visit_date: v})} type="date" />}
                {!isBuiltinFieldDisabled('concern','reason_for_visit') && <EditableField label="Reason for Visit" value={ef.reason_for_visit} editing={editing} onChange={v => setEditForm({...editForm, reason_for_visit: v})} />}
                {!isBuiltinFieldDisabled('concern','other_agencies') && <EditableField label="Other Agencies Involved" value={ef.other_agencies} editing={editing} onChange={v => setEditForm({...editForm, other_agencies: v})} multiline />}
              </FieldGroup>
              {builtinExtras('concern')}
            </TabsContent>

            {/* Caregiver */}
            <TabsContent value="caregiver" className={`m-0 ${pclass('caregiver')}`} {...pmount}>
              <div className="print-tab-heading">{builtinLabel('caregiver', 'Caregiver')}</div>
              <FieldGroup title="Primary Caregiver">
                {!isBuiltinFieldDisabled('caregiver','primary_caregiver_name') && <EditableField label="Name" icon={UserCheck} value={ef.primary_caregiver_name} editing={editing} onChange={v => setEditForm({...editForm, primary_caregiver_name: v})} />}
                {!isBuiltinFieldDisabled('caregiver','primary_caregiver_relationship') && <EditableField label="Relationship" value={ef.primary_caregiver_relationship} editing={editing} onChange={v => setEditForm({...editForm, primary_caregiver_relationship: v})} />}
                {!isBuiltinFieldDisabled('caregiver','primary_caregiver_phone') && <EditableField label="Phone" icon={Phone} value={ef.primary_caregiver_phone} editing={editing} onChange={v => setEditForm({...editForm, primary_caregiver_phone: v})} />}
                {!isBuiltinFieldDisabled('caregiver','primary_caregiver_address') && <EditableField label="Address" icon={Home} value={ef.primary_caregiver_address} editing={editing} onChange={v => setEditForm({...editForm, primary_caregiver_address: v})} />}
              </FieldGroup>
              {builtinExtras('caregiver')}
            </TabsContent>

            {/* Pharmacy */}
            <TabsContent value="pharmacy" className={`m-0 ${pclass('pharmacy')}`} {...pmount}>
              <div className="print-tab-heading">{builtinLabel('pharmacy', 'Pharmacy')}</div>
              <FieldGroup title="Pharmacy">
                {!isBuiltinFieldDisabled('pharmacy','pharmacy_name') && <EditableField label="Pharmacy Name" value={ef.pharmacy_name} editing={editing} onChange={v => setEditForm({...editForm, pharmacy_name: v})} />}
                {!isBuiltinFieldDisabled('pharmacy','pharmacy_address') && <EditableField label="Address" icon={MapPin} value={ef.pharmacy_address} editing={editing} onChange={v => setEditForm({...editForm, pharmacy_address: v})} />}
                {!isBuiltinFieldDisabled('pharmacy','pharmacy_phone') && <EditableField label="Phone" icon={Phone} value={ef.pharmacy_phone} editing={editing} onChange={v => setEditForm({...editForm, pharmacy_phone: v})} />}
              </FieldGroup>
              {builtinExtras('pharmacy')}
            </TabsContent>

            {/* Clinical (original tabs merged) */}
            <TabsContent value="clinical" className={`m-0 ${pclass('clinical')}`} {...pmount}>
              <div className="print-tab-heading">{builtinLabel('clinical', 'Clinical')}</div>
              <Tabs defaultValue={(() => {
                const order = ['vitals', 'medications', 'labs', 'timeline'];
                return order.find(k => !isBuiltinFieldDisabled('clinical', k)) || 'vitals';
              })()}>
                <TabsList className="mb-3">
                  {!isBuiltinFieldDisabled('clinical','vitals') && <TabsTrigger value="vitals">Vitals</TabsTrigger>}
                  {!isBuiltinFieldDisabled('clinical','medications') && <TabsTrigger value="medications">Meds</TabsTrigger>}
                  {!isBuiltinFieldDisabled('clinical','labs') && <TabsTrigger value="labs">Labs</TabsTrigger>}
                  {!isBuiltinFieldDisabled('clinical','timeline') && <TabsTrigger value="timeline">Timeline</TabsTrigger>}
                </TabsList>
                {!isBuiltinFieldDisabled('clinical','vitals') && (
                <TabsContent value="vitals" {...pmount}>
                  <div className="flex justify-end mb-2 no-print">
                    <Button size="sm" className="careiq-btn-primary" onClick={() => setShowAddVital(true)} data-testid="add-vital-btn"><Plus className="w-3 h-3 mr-1" />Record Vital</Button>
                  </div>
                  {vitals.length > 0 ? <div className="space-y-3">{vitals.slice(0,5).map(v => <div key={v.id} className="p-3 rounded-lg bg-[#F8FAFC] text-sm"><div className="text-xs text-[#64748B] mb-1">{new Date(v.recorded_at).toLocaleString()}</div><div className="grid grid-cols-4 gap-2">{v.blood_pressure_systolic && <div><span className="text-[#64748B] text-xs">BP</span><div className="font-medium">{v.blood_pressure_systolic}/{v.blood_pressure_diastolic}</div></div>}{v.heart_rate && <div><span className="text-[#64748B] text-xs">HR</span><div className="font-medium">{v.heart_rate}</div></div>}{v.temperature && <div><span className="text-[#64748B] text-xs">Temp</span><div className="font-medium">{v.temperature}F</div></div>}{v.oxygen_saturation && <div><span className="text-[#64748B] text-xs">SpO2</span><div className="font-medium">{v.oxygen_saturation}%</div></div>}</div></div>)}</div> : <EmptyState icon={Activity} text="No vitals recorded" />}
                </TabsContent>
                )}
                {!isBuiltinFieldDisabled('clinical','medications') && (
                <TabsContent value="medications" {...pmount}>{medications.length > 0 ? <div className="space-y-2">{medications.map(m => <div key={m.id} className="flex justify-between p-3 rounded-lg bg-[#F8FAFC]"><div><div className="font-medium text-sm">{m.name}</div><div className="text-xs text-[#64748B]">{m.dosage} | {m.frequency}</div></div><span className={`text-xs px-2 py-0.5 rounded-full ${m.is_active ? 'bg-[#166534]/10 text-[#166534]' : 'bg-[#64748B]/10 text-[#64748B]'}`}>{m.is_active ? 'Active' : 'Inactive'}</span></div>)}</div> : <EmptyState icon={Pill} text="No medications" />}</TabsContent>
                )}
                {!isBuiltinFieldDisabled('clinical','labs') && (
                <TabsContent value="labs" {...pmount}>{labs.length > 0 ? <div className="space-y-2">{labs.map(l => <div key={l.id} className="p-3 rounded-lg bg-[#F8FAFC]"><div className="flex justify-between"><span className="font-medium text-sm">{l.test_name}</span><span className={`text-xs px-2 py-0.5 rounded-full ${l.status==='completed' ? 'bg-[#166534]/10 text-[#166534]' : 'bg-[#92400E]/10 text-[#92400E]'}`}>{l.status}</span></div></div>)}</div> : <EmptyState icon={Heart} text="No labs" />}</TabsContent>
                )}
                {!isBuiltinFieldDisabled('clinical','timeline') && (
                <TabsContent value="timeline" {...pmount}>{timeline.length > 0 ? <div className="space-y-3">{timeline.map(e => <div key={e.id} className="flex gap-3"><FileText className="w-4 h-4 text-[#0E7490] mt-1 shrink-0" /><div><div className="text-sm font-medium">{e.title}</div><div className="text-xs text-[#64748B]">{e.description}</div><div className="text-[10px] text-[#94A3B8]">{new Date(e.date).toLocaleDateString()}</div></div></div>)}</div> : <EmptyState icon={Clock} text="No events" />}</TabsContent>
                )}
              </Tabs>
              {builtinExtras('clinical')}
            </TabsContent>

            {/* Files Tab */}
            <TabsContent value="files" className={`m-0 ${pclass('files')}`} {...pmount}>
              <div className="print-tab-heading">{builtinLabel('files', 'Files')}</div>
              <div className="flex items-center justify-between mb-3 no-print">
                <p className="text-sm text-[#64748B]">{patientForms.length} submitted form(s) | {files.uploads?.length || 0} upload(s) | {files.folders?.length || 0} folder(s)</p>
                <div className="flex gap-2">
                  <PatientFileUpload
                    patientId={patientId}
                    folders={files.folders || []}
                    onUploaded={async () => {
                      const r = await patientApi.getFiles(patientId);
                      setFiles(r.data || { folders: [], files: [], uploads: [] });
                    }}
                  />
                  <Button size="sm" variant="outline" onClick={() => setShowNewFolder(true)} data-testid="new-folder-btn"><FolderPlus className="w-3 h-3 mr-1" />New Folder</Button>
                  <Link to={`/forms?patientId=${patientId}`}><Button size="sm" className="careiq-btn-primary"><FormInput className="w-3 h-3 mr-1" />Fill Form</Button></Link>
                </div>
              </div>

              {/* Uploaded Files (images, scans, PDFs) */}
              {!isBuiltinFieldDisabled('files','uploaded_files') && (files.uploads || []).length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-[#0A2342] mb-2 uppercase tracking-wider flex items-center gap-1">
                    <Paperclip className="w-3 h-3" />Uploaded Files
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="uploaded-files-grid">
                    {(files.uploads || []).map((u) => (
                      <div key={u.id} className="group relative rounded-lg border border-[#E2E8F0] bg-white overflow-hidden hover:shadow-md transition-shadow">
                        <div className="aspect-square bg-[#F8FAFC] flex items-center justify-center relative">
                          {u.mime_type?.startsWith('image/') ? (
                            <UploadThumb uploadId={u.id} patientId={patientId} filename={u.filename} />
                          ) : (
                            <div className="flex flex-col items-center gap-1 text-[#64748B] p-3">
                              <FileText className="w-8 h-8" />
                              <div className="text-[9px] text-center truncate w-full">{u.mime_type}</div>
                            </div>
                          )}
                          {u.source && u.source !== 'file' && (
                            <div className="absolute top-1 right-1 bg-white/90 rounded px-1 py-0.5 text-[8px] text-[#0E7490] flex items-center gap-0.5">
                              <Camera className="w-2.5 h-2.5" />{u.source === 'webcam' ? 'Webcam' : 'Camera'}
                            </div>
                          )}
                        </div>
                        <div className="p-2">
                          <div className="text-[10px] font-medium text-[#0A2342] truncate" title={u.filename}>{u.filename}</div>
                          <div className="text-[9px] text-[#64748B] truncate">{u.uploaded_at ? new Date(u.uploaded_at).toLocaleDateString() : ''} · {u.uploaded_by_name || u.uploaded_by_role}</div>
                          {u.note && <div className="text-[9px] text-[#64748B] truncate italic" title={u.note}>{u.note}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submitted Forms */}
              {!isBuiltinFieldDisabled('files','form_submissions') && patientForms.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-[#0A2342] mb-2 uppercase tracking-wider">Submitted Forms</h4>
                  <div className="space-y-2">
                    {patientForms.map((sub) => (
                      <div key={sub.id} className="flex items-center justify-between p-3 rounded-lg bg-[#F8FAFC] border hover:bg-[#F1F5F9] transition-colors" data-testid={`submitted-form-${sub.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="w-4 h-4 text-[#0E7490] shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-[#0A2342] truncate">{sub.form_name || 'Untitled Form'}</div>
                            <div className="flex items-center gap-2 text-[10px] text-[#64748B]">
                              <span>{sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : ''}</span>
                              {sub.submitted_by_name && <span>by {sub.submitted_by_name}</span>}
                              {sub.edit_history?.length > 0 && <span className="px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] rounded">Edited {sub.edit_history.length}x</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 no-print">
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-[#0E7490]" onClick={() => setShowSubmission(sub)} data-testid={`view-sub-${sub.id}`}>
                            <Eye className="w-3 h-3 mr-1" />View
                          </Button>
                          <Link to={`/forms/${sub.form_id}/submissions/${sub.id}/edit`}>
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-[#0E7490]" data-testid={`edit-sub-${sub.id}`}>
                              <Edit className="w-3 h-3 mr-1" />Edit
                            </Button>
                          </Link>
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-[#64748B]" onClick={() => {
                            setShowSubmission(sub);
                            setTimeout(() => window.print(), 500);
                          }} data-testid={`print-sub-${sub.id}`}>
                            <Printer className="w-3 h-3 mr-1" />Print
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Folders */}
              {!isBuiltinFieldDisabled('files','folders') && (files.folders || []).map(folder => (
                <div key={folder.id} className="mb-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-[#0A2342] mb-1"><Folder className="w-4 h-4 text-[#CA8A04]" />{folder.name}</div>
                  <div className="ml-6 space-y-1">
                    {(files.files || []).filter(f => f.folder_id === folder.id).map(file => (
                      <div key={file.id} className="flex items-center justify-between p-2 rounded bg-[#F8FAFC] text-xs hover:bg-[#F1F5F9] cursor-pointer" onClick={() => {
                        const sub = patientForms.find(pf => pf.id === file.id);
                        if (sub) setShowSubmission(sub);
                      }}>
                        <div className="flex items-center gap-2"><FileText className="w-3 h-3 text-[#0E7490]" /><span className="font-medium">{file.form_name}</span></div>
                        <div className="flex items-center gap-2">
                          <span className="text-[#64748B]">{file.submitted_at ? new Date(file.submitted_at).toLocaleDateString() : ''}</span>
                          <Link to={`/forms/${file.form_id}/submissions/${file.id}/edit`} onClick={e => e.stopPropagation()} className="no-print">
                            <Button size="sm" variant="ghost" className="h-5 text-[9px] text-[#0E7490]"><Edit className="w-2.5 h-2.5 mr-0.5" />Edit</Button>
                          </Link>
                          {(files.folders || []).length > 0 && (
                            <div onClick={e => e.stopPropagation()} className="no-print">
                              <Select onValueChange={v => handleMoveFile(file.id, v)}>
                                <SelectTrigger className="h-6 w-24 text-[10px]"><SelectValue placeholder="Move to..." /></SelectTrigger>
                                <SelectContent>{(files.folders || []).map(fo => <SelectItem key={fo.id} value={fo.id} className="text-xs">{fo.name}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {/* Unfiled */}
              <div className="mb-2"><span className="text-xs font-medium text-[#94A3B8]">Unfiled</span></div>
              <div className="space-y-1">
                {(files.files || []).filter(f => !f.folder_id).map(file => (
                  <div key={file.id} className="flex items-center justify-between p-2 rounded bg-[#F8FAFC] text-xs hover:bg-[#F1F5F9] cursor-pointer" onClick={() => {
                    const sub = patientForms.find(pf => pf.id === file.id);
                    if (sub) setShowSubmission(sub);
                  }}>
                    <div className="flex items-center gap-2"><FileText className="w-3 h-3 text-[#0E7490]" /><span className="font-medium">{file.form_name}</span></div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#64748B]">{file.submitted_at ? new Date(file.submitted_at).toLocaleDateString() : ''}</span>
                      <Link to={`/forms/${file.form_id}/submissions/${file.id}/edit`} onClick={e => e.stopPropagation()} className="no-print">
                        <Button size="sm" variant="ghost" className="h-5 text-[9px] text-[#0E7490]"><Edit className="w-2.5 h-2.5 mr-0.5" />Edit</Button>
                      </Link>
                      {(files.folders || []).length > 0 && (
                        <div onClick={e => e.stopPropagation()} className="no-print">
                          <Select onValueChange={v => handleMoveFile(file.id, v)}>
                            <SelectTrigger className="h-6 w-24 text-[10px]"><SelectValue placeholder="Move to..." /></SelectTrigger>
                            <SelectContent>{(files.folders || []).map(fo => <SelectItem key={fo.id} value={fo.id} className="text-xs">{fo.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {builtinExtras('files')}
            </TabsContent>

            {/* Custom Tabs — skip built-in overrides (those are rendered inside native tabs) */}
            {customTabs.filter(t => t.type !== 'builtin_override' && !t.hidden).map(ct => (
              <TabsContent key={ct.id} value={`custom-${ct.id}`} className={`m-0 ${pclass(`custom-${ct.id}`)}`} {...pmount}>
                <div className="print-tab-heading">{ct.name}</div>
                <div className="flex items-center justify-between mb-3 no-print">
                  <h3 className="font-semibold text-[#0A2342]">{ct.name}</h3>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => navigate('/admin/patient-tabs')} title="Edit tab fields" data-testid={`edit-tab-${ct.id}`}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteTab(ct.id)} className="text-[#991B1B]">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
                {ct.type === 'form_linked' && ct.form_id ? (
                  (() => {
                    const linkedForm = allForms.find(ff => ff.id === ct.form_id);
                    if (!linkedForm) return <p className="text-sm text-[#94A3B8] py-4 text-center">Linked form not found</p>;
                    const formSubs = patientForms.filter(pf => pf.form_id === ct.form_id);
                    const latestSub = formSubs[0];
                    return (
                      <div className="space-y-4" data-testid={`custom-form-tab-${ct.id}`}>
                        {latestSub ? (
                          <>
                            <div className="flex items-center justify-between gap-2 flex-wrap no-print">
                              <div className="text-xs text-[#64748B]">
                                Last updated: {latestSub.submitted_at ? new Date(latestSub.submitted_at).toLocaleString() : ''}
                                {latestSub.submitted_by_name && <span> by {latestSub.submitted_by_name}</span>}
                              </div>
                              <div className="flex gap-1.5 flex-wrap">
                                <Link to={`/forms/${ct.form_id}/submissions/${latestSub.id}/edit`}><Button size="sm" variant="outline" className="h-9 sm:h-7 text-xs flex-1 sm:flex-none" data-testid={`update-record-${ct.id}`}><Edit className="w-3 h-3 mr-1" />Update or Print Record</Button></Link>
                                <Link to={`/forms/${ct.form_id}/fill?patientId=${patientId}`}><Button size="sm" className="careiq-btn-primary h-9 sm:h-7 text-xs flex-1 sm:flex-none" data-testid={`new-entry-${ct.id}`}><Plus className="w-3 h-3 mr-1" />New Entry</Button></Link>
                              </div>
                            </div>
                            <FormFilledView submission={latestSub} form={allForms.find(ff => ff.id === ct.form_id)} />
                          </>
                        ) : (
                          <div className="text-center py-8" data-testid={`tab-empty-${ct.id}`}>
                            <FormInput className="w-10 h-10 mx-auto mb-3 text-[#94A3B8]" />
                            <p className="text-sm text-[#94A3B8] mb-3">No records yet</p>
                            <Link to={`/forms/${ct.form_id}/fill?patientId=${patientId}`}><Button size="sm" className="careiq-btn-primary"><Plus className="w-3 h-3 mr-1" />Add First Record</Button></Link>
                          </div>
                        )}
                        {formSubs.length > 1 && (
                          <div className="border-t border-[#E2E8F0] pt-3">
                            <h4 className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-2">History ({formSubs.length - 1} previous)</h4>
                            <div className="space-y-1">
                              {formSubs.slice(1).map((sub) => (
                                <div key={sub.id} className="flex items-center justify-between p-2 rounded bg-[#FAFBFC] text-xs cursor-pointer hover:bg-[#F1F5F9] transition-colors" onClick={() => setShowSubmission(sub)}>
                                  <span className="text-[#64748B]">{sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : ''}</span>
                                  <div className="flex items-center gap-2">
                                    {sub.submitted_by_name && <span className="text-[#94A3B8]">{sub.submitted_by_name}</span>}
                                    <Eye className="w-3 h-3 text-[#94A3B8]" />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()
                ) : (
                  <PatientCustomTabContent tab={ct} patientId={patientId} canEdit={canEdit} editing={editing} />
                )}
              </TabsContent>
            ))}

            {/* Patient Record Tabs (from published forms) */}
            {allForms.filter(f => f.status === 'published' && !customTabs.some(ct => ct.form_id === f.id)).map(f => {
              const formSubs = patientForms.filter(pf => pf.form_id === f.id);
              const latestSub = formSubs[0]; // Most recent submission
              return (
                <TabsContent key={`form-${f.id}`} value={`form-${f.id}`} className={`m-0 ${pclass(`form-${f.id}`)}`} {...pmount}>
                  <div className="print-tab-heading">{f.name}</div>
                  <div className="space-y-4">
                    {/* Latest Record Display — shown like native patient data */}
                    {latestSub ? (
                      <>
                        <div className="flex items-center justify-between gap-2 flex-wrap no-print">
                          <div className="text-xs text-[#64748B]">
                            Last updated: {latestSub.submitted_at ? new Date(latestSub.submitted_at).toLocaleString() : ''} 
                            {latestSub.submitted_by_name && <span> by {latestSub.submitted_by_name}</span>}
                            {latestSub.edit_history?.length > 0 && <span className="ml-2 px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] rounded text-[9px]">Edited {latestSub.edit_history.length}x</span>}
                          </div>
                          <div className="flex gap-1.5 flex-wrap">
                            <Link to={`/forms/${f.id}/submissions/${latestSub.id}/edit`}>
                              <Button size="sm" variant="outline" className="h-9 sm:h-7 text-xs"><Edit className="w-3 h-3 mr-1" />Update or Print Record</Button>
                            </Link>
                            <Link to={`/forms/${f.id}/fill?patientId=${patientId}`}>
                              <Button size="sm" className="careiq-btn-primary h-9 sm:h-7 text-xs"><Plus className="w-3 h-3 mr-1" />New Entry</Button>
                            </Link>
                          </div>
                        </div>
                        {/* Render latest record data as native fields — grouped by form sections (collapsible) */}
                        <FormFilledView submission={latestSub} form={f} />
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-sm text-[#94A3B8] mb-3">No records yet</p>
                        <Link to={`/forms/${f.id}/fill?patientId=${patientId}`}><Button size="sm" className="careiq-btn-primary"><Plus className="w-3 h-3 mr-1" />Add First Record</Button></Link>
                      </div>
                    )}

                    {/* History — previous entries */}
                    {formSubs.length > 1 && (
                      <div className="border-t border-[#E2E8F0] pt-3">
                        <h4 className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-2">History ({formSubs.length - 1} previous)</h4>
                        <div className="space-y-1">
                          {formSubs.slice(1).map((sub) => (
                            <div key={sub.id} className="flex items-center justify-between p-2 rounded bg-[#FAFBFC] text-xs cursor-pointer hover:bg-[#F1F5F9] transition-colors" onClick={() => setShowSubmission(sub)}>
                              <span className="text-[#64748B]">{sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : ''}</span>
                              <div className="flex items-center gap-2">
                                {sub.submitted_by_name && <span className="text-[#94A3B8]">{sub.submitted_by_name}</span>}
                                <Eye className="w-3 h-3 text-[#94A3B8]" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>
              );
            })}
          </CardContent>
        </Tabs>
      </Card>

      {/* AI Summary Sidebar */}
      <Card className="careiq-card no-print">
        <CardHeader><CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2"><Brain className="w-5 h-5 text-[#0E7490]" />AI Patient Briefing</CardTitle></CardHeader>
        <CardContent>
          {aiSummary ? <ScrollArea className="h-36"><div className="text-sm text-[#334155] whitespace-pre-wrap">{aiSummary}</div></ScrollArea> : (
            <Button onClick={generateAiSummary} disabled={aiLoading} className="careiq-btn-secondary" data-testid="generate-ai-summary">
              {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}Generate Briefing
            </Button>
          )}
        </CardContent>
      </Card>

      {/* New Folder Dialog */}
      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent className="sm:max-w-sm"><DialogHeader><DialogTitle>Create Folder</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2"><Input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="Folder name" data-testid="folder-name-input" />
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowNewFolder(false)}>Cancel</Button><Button className="careiq-btn-primary" onClick={handleCreateFolder}>Create</Button></div></div>
        </DialogContent>
      </Dialog>

      {/* Add Tab Dialog */}
      <Dialog open={showAddTab} onOpenChange={setShowAddTab}>
        <DialogContent className="sm:max-w-md" data-testid="add-tab-dialog"><DialogHeader><DialogTitle>Add Patient Record Tab</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Tab Name *</Label><Input value={newTabName} onChange={e => setNewTabName(e.target.value)} placeholder="e.g. Assessments" data-testid="tab-name-input" /></div>
            <div><Label>Link to Form (optional)</Label>
              <Select value={newTabFormId || 'none'} onValueChange={(v) => setNewTabFormId(v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="None — custom tab" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{allForms.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[10px] text-[#94A3B8] mt-1">Link a form to show its submissions in this tab</p>
            </div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowAddTab(false)}>Cancel</Button><Button className="careiq-btn-primary" onClick={handleAddTab} data-testid="save-tab-btn">Add Tab</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record Vital Dialog */}
      <Dialog open={showAddVital} onOpenChange={setShowAddVital}>
        <DialogContent className="sm:max-w-md" data-testid="add-vital-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Thermometer className="w-5 h-5 text-[#0E7490]" />Record Vital Signs</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Date</Label><Input type="date" value={vitalForm.recorded_at ? vitalForm.recorded_at.split('T')[0] : ''} onChange={e => { const t = vitalForm.recorded_at ? vitalForm.recorded_at.split('T')[1] || '12:00' : '12:00'; setVitalForm({...vitalForm, recorded_at: e.target.value ? `${e.target.value}T${t}` : ''}); }} data-testid="vital-date" /></div>
              <div><Label className="text-xs">Time</Label><Input type="time" value={vitalForm.recorded_at ? (vitalForm.recorded_at.split('T')[1] || '') : ''} onChange={e => { const d = vitalForm.recorded_at ? vitalForm.recorded_at.split('T')[0] : new Date().toISOString().split('T')[0]; setVitalForm({...vitalForm, recorded_at: `${d}T${e.target.value}`}); }} data-testid="vital-time" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Systolic BP</Label><Input type="number" value={vitalForm.blood_pressure_systolic} onChange={e => setVitalForm({...vitalForm, blood_pressure_systolic: e.target.value})} placeholder="120" data-testid="vital-systolic" /></div>
              <div><Label className="text-xs">Diastolic BP</Label><Input type="number" value={vitalForm.blood_pressure_diastolic} onChange={e => setVitalForm({...vitalForm, blood_pressure_diastolic: e.target.value})} placeholder="80" data-testid="vital-diastolic" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Heart Rate (bpm)</Label><Input type="number" value={vitalForm.heart_rate} onChange={e => setVitalForm({...vitalForm, heart_rate: e.target.value})} placeholder="72" data-testid="vital-hr" /></div>
              <div><Label className="text-xs">Temperature (F)</Label><Input type="number" step="0.1" value={vitalForm.temperature} onChange={e => setVitalForm({...vitalForm, temperature: e.target.value})} placeholder="98.6" data-testid="vital-temp" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">SpO2 (%)</Label><Input type="number" value={vitalForm.oxygen_saturation} onChange={e => setVitalForm({...vitalForm, oxygen_saturation: e.target.value})} placeholder="98" data-testid="vital-spo2" /></div>
              <div><Label className="text-xs">Weight (lbs)</Label><Input type="number" value={vitalForm.weight} onChange={e => setVitalForm({...vitalForm, weight: e.target.value})} placeholder="150" /></div>
              <div><Label className="text-xs">Resp Rate</Label><Input type="number" value={vitalForm.respiratory_rate} onChange={e => setVitalForm({...vitalForm, respiratory_rate: e.target.value})} placeholder="16" /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddVital(false)}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleAddVital} data-testid="save-vital-btn">Record Vital</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Submission Dialog */}
      <Dialog open={!!showSubmission} onOpenChange={() => setShowSubmission(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="view-submission-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#0E7490]" />Form Submission</DialogTitle></DialogHeader>
          {showSubmission && (
            <div className="space-y-3 mt-2">
              <div className="text-sm text-[#64748B]">
                <span className="font-medium text-[#0A2342]">{showSubmission.form_name}</span>
                <span className="ml-2">{showSubmission.submitted_at ? new Date(showSubmission.submitted_at).toLocaleString() : ''}</span>
              </div>
              {showSubmission.edit_history?.length > 0 && (
                <div className="bg-[#FEF3C7] rounded-lg p-2">
                  <div className="text-[10px] font-medium text-[#92400E] mb-1">Edit History ({showSubmission.edit_history.length} edit{showSubmission.edit_history.length > 1 ? 's' : ''})</div>
                  {showSubmission.edit_history.map((h, i) => (
                    <div key={i} className="text-[10px] text-[#92400E]">{h.edited_by_name || h.edited_by_role} — {new Date(h.edited_at).toLocaleString()}</div>
                  ))}
                </div>
              )}
              <div className="space-y-2">
                {(() => {
                  // Use resolved_responses from backend if available (properly resolved labels)
                  const resolved = showSubmission.resolved_responses;
                  const renderValue = (val) => {
                    if (val === null || val === undefined || val === '') return <span className="text-[#94A3B8]">—</span>;
                    if (typeof val === 'string' && val.startsWith('data:image')) {
                      return <img src={val} alt="Signature" className="max-h-20 border border-[#E2E8F0] rounded bg-white mt-1" />;
                    }
                    if (typeof val === 'object') return <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(val, null, 2)}</pre>;
                    return String(val);
                  };
                  if (resolved && Object.keys(resolved).length > 0) {
                    return Object.entries(resolved).map(([label, val]) => (
                      <div key={label} className="flex gap-2 text-sm border-b border-[#E2E8F0] pb-1">
                        <span className="text-[#64748B] font-medium min-w-[120px] shrink-0">{label}:</span>
                        <span className="text-[#0A2342] flex-1">{renderValue(val)}</span>
                      </div>
                    ));
                  }
                  // Fallback: show raw responses
                  return Object.entries(showSubmission.responses || {}).map(([k, v]) => (
                    <div key={k} className="flex gap-2 text-sm border-b border-[#E2E8F0] pb-1">
                      <span className="text-[#64748B] font-medium min-w-[120px] shrink-0">{k}:</span>
                      <span className="text-[#0A2342] flex-1">{renderValue(v)}</span>
                    </div>
                  ));
                })()}
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="print-submission-dialog-btn"><Printer className="w-3 h-3 mr-1" />Print</Button>
                <Link to={`/forms/${showSubmission.form_id}/submissions/${showSubmission.id}/edit`}>
                  <Button size="sm" className="careiq-btn-primary" data-testid="edit-submission-btn"><Edit className="w-3 h-3 mr-1" />Edit</Button>
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Selected Tabs Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="sm:max-w-md" data-testid="print-tabs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-[#0E7490]" />Share Patient Record
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Delivery mode selector */}
            <div>
              <Label className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Delivery</Label>
              <div className="grid grid-cols-3 gap-1 mt-1 p-1 bg-[#F1F5F9] rounded-lg">
                {[
                  { k: 'print', label: 'Print', icon: Printer },
                  { k: 'download', label: 'PDF', icon: Download },
                  { k: 'email', label: 'Email', icon: Send },
                ].map(m => (
                  <button
                    key={m.k}
                    type="button"
                    onClick={() => setDeliveryMode(m.k)}
                    data-testid={`delivery-mode-${m.k}`}
                    className={`flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      deliveryMode === m.k
                        ? 'bg-white text-[#0A2342] shadow-sm'
                        : 'text-[#64748B] hover:text-[#0A2342]'
                    }`}
                  >
                    <m.icon className="w-3.5 h-3.5" />{m.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[#94A3B8] mt-1.5">
                {deliveryMode === 'print' && 'Open the browser print dialog (also lets you Save as PDF).'}
                {deliveryMode === 'download' && 'Generate a clean HIPAA-audited PDF and download it locally.'}
                {deliveryMode === 'email' && 'Queue a PDF attachment to a referring provider via secure email.'}
              </p>
            </div>

            {/* Tab selection */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Sections ({allPrintTabs.length})</Label>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAllPrintTabs(true)} data-testid="print-select-all">Select all</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAllPrintTabs(false)} data-testid="print-select-none">None</Button>
                </div>
              </div>
              <div className="space-y-1 mt-1 max-h-[220px] overflow-y-auto border rounded-lg p-2">
                {allPrintTabs.length === 0 ? (
                  <p className="text-xs text-[#94A3B8] text-center py-2">No sections available</p>
                ) : allPrintTabs.map(t => (
                  <label
                    key={t.key}
                    className="flex items-center gap-2 text-sm cursor-pointer p-1.5 hover:bg-[#F8FAFC] rounded"
                    data-testid={`print-tab-row-${t.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!selectedPrintTabs[t.key]}
                      onChange={() => togglePrintTab(t.key)}
                      className="rounded"
                      data-testid={`print-tab-checkbox-${t.key}`}
                    />
                    <span className="flex-1">{t.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Email fields — only when delivery is email */}
            {deliveryMode === 'email' && (
              <div className="space-y-2 pt-1 border-t border-[#E2E8F0]" data-testid="email-fields">
                <div>
                  <Label className="text-xs font-medium text-[#0A2342]">Recipient email *</Label>
                  <Input
                    type="email"
                    placeholder="provider@clinic.com"
                    value={emailForm.recipient_email}
                    onChange={e => setEmailForm({...emailForm, recipient_email: e.target.value})}
                    className="h-9 text-sm mt-1"
                    data-testid="email-recipient-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-[#0A2342]">Recipient name (optional)</Label>
                  <Input
                    placeholder="Dr. Jane Smith"
                    value={emailForm.recipient_name}
                    onChange={e => setEmailForm({...emailForm, recipient_name: e.target.value})}
                    className="h-9 text-sm mt-1"
                    data-testid="email-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-[#0A2342]">Message (optional)</Label>
                  <Textarea
                    placeholder="Requesting consult for patient..."
                    value={emailForm.message}
                    onChange={e => setEmailForm({...emailForm, message: e.target.value})}
                    className="text-sm mt-1 min-h-[60px]"
                    data-testid="email-message-input"
                  />
                </div>
                <p className="text-[10px] text-[#94A3B8]">
                  The recipient will receive a PDF attachment. The action is logged to the HIPAA audit trail.
                </p>
              </div>
            )}

            {/* Action row */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowPrintDialog(false)} disabled={sendingPdf} data-testid="print-cancel-btn">Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handlePrintSelected} disabled={sendingPdf} data-testid="print-confirm-btn">
                {sendingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (
                  deliveryMode === 'print' ? <Printer className="w-4 h-4 mr-2" /> :
                  deliveryMode === 'download' ? <Download className="w-4 h-4 mr-2" /> :
                  <Send className="w-4 h-4 mr-2" />
                )}
                {deliveryMode === 'print' ? 'Print' : deliveryMode === 'download' ? 'Download PDF' : 'Send PDF'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExport} onOpenChange={setShowExport}>
        <DialogContent className="sm:max-w-md" data-testid="export-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Download className="w-5 h-5 text-[#0E7490]" />Export Patient Data</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-[#64748B]">Export includes demographics, emergency contact, vitals, and selected form submissions.</p>
            <div>
              <Label className="text-sm font-medium">Include Form Submissions:</Label>
              <div className="space-y-1 mt-1 max-h-[200px] overflow-y-auto border rounded-lg p-2">
                {[...new Set(patientForms.map(pf => pf.form_id))].map(fid => {
                  const formName = allForms.find(f => f.id === fid)?.name || patientForms.find(pf => pf.form_id === fid)?.form_name || fid;
                  const count = patientForms.filter(pf => pf.form_id === fid).length;
                  return (
                    <label key={fid} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-[#F8FAFC] rounded">
                      <input type="checkbox" checked={exportForms.includes(fid)} onChange={() => setExportForms(prev => prev.includes(fid) ? prev.filter(id => id !== fid) : [...prev, fid])} className="rounded" />
                      <span className="flex-1">{formName}</span>
                      <span className="text-[10px] text-[#64748B]">{count} submission{count !== 1 ? 's' : ''}</span>
                    </label>
                  );
                })}
                {patientForms.length === 0 && <p className="text-xs text-[#94A3B8] text-center py-2">No form submissions</p>}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowExport(false)}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleExport} data-testid="confirm-export-btn"><Download className="w-4 h-4 mr-2" />Export</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PrintPreviewDialog
        open={showPrintPreview}
        onClose={handleClosePrintPreview}
        contentSelector='[data-testid="patient-profile"]'
        bodyClass="patient-print-mode"
        title={`Print Preview — ${patient?.first_name || ''} ${patient?.last_name || ''}`}
      />

      {/* Voice note recorder — pre-attaches this patient */}
      <VoiceNoteDialog
        open={showVoiceNote}
        onOpenChange={setShowVoiceNote}
        patientId={patient?.id}
        patientName={`${patient?.first_name || ''} ${patient?.last_name || ''}`.trim()}
      />
    </div>
  );
}

function FieldGroup({ title, children }) {
  return (
    <div className="p-4 rounded-lg border border-[#E2E8F0] space-y-2">
      <h3 className="text-sm font-semibold text-[#0A2342] mb-2">{title}</h3>
      {children}
    </div>
  );
}

function EditableField({ label, icon: Icon, value, editing, onChange, type = 'text', multiline = false }) {
  if (editing) {
    return (
      <div className="flex items-start gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#94A3B8] mt-2 shrink-0" />}
        <div className="flex-1">
          <Label className="text-[10px] text-[#94A3B8]">{label}</Label>
          {multiline ? <Textarea value={value || ''} onChange={e => onChange(e.target.value)} className="min-h-[60px] text-sm" /> : <Input type={type} value={value || ''} onChange={e => onChange(e.target.value)} className="text-sm h-8" />}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-sm">
      {Icon && <Icon className="w-4 h-4 text-[#94A3B8] shrink-0" />}
      <span className="text-[#64748B] min-w-[80px]">{label}:</span>
      <span className="text-[#0A2342]">{value || '—'}</span>
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return <div className="text-center py-8 text-[#94A3B8]"><Icon className="w-8 h-8 mx-auto mb-2" /><p className="text-sm">{text}</p></div>;
}
